import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

/** After this long a run is reassuringly annotated as safe to leave rather than looking hung. */
const REASSURE_AFTER_SECONDS = 120;

/**
 * Live "how long has this run been going" clock. Self-ticks every second (so the user always has a
 * sense of time, not just a spinner) and, once `reassure` passes REASSURE_AFTER_SECONDS, adds a note
 * that a long run is fine to leave — directly answering "is it stuck, or just slow?".
 *
 * Single home for this component. It previously existed as two byte-identical copies (WorkspaceTopBar
 * and ExecutionFeed), each with its own interval.
 */
export function RunElapsed({ startedAt, reassure }: { startedAt: number; reassure?: boolean }) {
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		const t = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(t);
	}, []);
	const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
	const h = Math.floor(secs / 3600);
	const m = Math.floor((secs % 3600) / 60);
	const s = secs % 60;
	const label = h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}:${String(s).padStart(2, "0")}`;
	return (
		<span className="inline-flex items-center gap-1 font-mono text-[11px] tabular-nums text-muted-foreground">
			<Clock className="h-3 w-3" />
			{label}
			{reassure && secs >= REASSURE_AFTER_SECONDS && (
				<span className="hidden font-sans text-muted-foreground/80 lg:inline">
					· still running — safe to keep working
				</span>
			)}
		</span>
	);
}
